import React, { useCallback } from 'react'
import styled from '@emotion/styled'
import { StyledHTMLElement } from 'shop/theme/types'
import { Promotion, PromotionDiscountType } from 'shop/types/common'
import { CSSProperties } from '@emotion/serialize'
import { formatMoney } from 'shop/utils/common'

type Styles = {
  position: CSSProperties['position']
  top: string
  left: string
  right: string
  bottom: string
  padding: string
  fontSize: string
  zIndex: number
}

interface PromoTagProps {
  promotion: Promotion
  styles?: Styles
}

// Extract default styles as constant to prevent recreation on every render
const defaultStyles: Styles = {
  position: 'absolute',
  top: '4px',
  right: '4px',
  left: 'auto',
  bottom: 'auto',
  padding: '4px 6px',
  fontSize: '10px',
  zIndex: 1
}

const PromoTag: React.FC<PromoTagProps> = ({
  promotion,
  styles = defaultStyles
}) => {
  const { amount, discountType } = promotion

  const formatPromoText = useCallback(() => {
    if (discountType === PromotionDiscountType.PERCENTAGE) {
      return `${amount}% off`
    } else if (discountType === PromotionDiscountType.BUY_ONE_SPECIAL) {
      if (amount === '100') {
        return 'Buy 1, Get 1 Free'
      } else {
        return `${amount}% off 2nd item`
      }
    } else if (discountType === PromotionDiscountType.FIXED) {
      return `${formatMoney(amount, { stripTrailingZeros: true })} off`
    } else {
      return null
    }
  }, [amount, discountType])

  const promoText = formatPromoText()

  // Early return if formatPromoText returns null
  if (!promoText) {
    return null
  }

  return (
    <PromoTagContainer data-testid='product-promo-tag' styles={styles}>
      {promoText}
    </PromoTagContainer>
  )
}

const PromoTagContainer = styled.div<
  StyledHTMLElement & {
    styles: Styles
  }
>(({ theme, styles }) => ({
  backgroundColor: '#EF0107',
  color: theme.colors.white,
  borderRadius: '4px',
  minWidth: 'fit-content',
  height: 'fit-content',
  ...styles
}))

export default PromoTag
